import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const today = new Date().toISOString().slice(0, 10);
const thisMonth = new Date().toISOString().slice(0, 7);

export default function HRDashboardPage() {
  const navigate = useNavigate();

  const { data: employees = [] } = useQuery({
    queryKey: ['hr-employees'],
    queryFn: () => api.get('/hr/employees').then(r => r.data),
  });

  const { data: todayAtt = [] } = useQuery({
    queryKey: ['hr-att-today'],
    queryFn: () => api.get(`/hr/attendance?month=${thisMonth}`).then(r =>
      r.data.filter(a => a.date?.slice(0, 10) === today)
    ),
  });

  const { data: pendingLeaves = [] } = useQuery({
    queryKey: ['hr-leaves-pending'],
    queryFn: () => api.get('/hr/leaves?status=pending').then(r => r.data),
  });

  const { data: payroll = [] } = useQuery({
    queryKey: ['hr-payroll-month'],
    queryFn: () => api.get(`/hr/payroll?month=${thisMonth}`).then(r => r.data),
  });

  const { data: holidays = [] } = useQuery({
    queryKey: ['hr-holidays'],
    queryFn: () => api.get('/hr/holidays').then(r => r.data),
  });

  const totalEmp = employees.length;
  const presentToday = todayAtt.filter(a => a.status === 'present' || a.status === 'half_day').length;
  const absentToday = totalEmp - todayAtt.length;
  const payrollDone = payroll.length;

  const upcomingHolidays = holidays.filter(h => h.holiday_date?.slice(0, 10) >= today).slice(0, 3);

  return (
    <div>
      <div className="section-head" style={{ marginBottom: 20 }}>
        <h2>HR Dashboard</h2>
        <span style={{ fontSize: 12, color: 'var(--text2)' }}>
          {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </span>
      </div>

      <div className="kpi-grid">
        <div className="kpi" style={{ cursor: 'pointer' }} onClick={() => navigate('/hr/employees')}>
          <div className="kpi-label">Total Employees</div>
          <div className="kpi-val">{totalEmp}</div>
          <div className="kpi-sub">Active</div>
        </div>
        <div className="kpi" style={{ cursor: 'pointer' }} onClick={() => navigate('/hr/attendance')}>
          <div className="kpi-label">Present Today</div>
          <div className="kpi-val" style={{ color: 'var(--green)' }}>{presentToday}</div>
          <div className="kpi-sub">Checked in</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Absent Today</div>
          <div className="kpi-val" style={{ color: absentToday > 0 ? 'var(--red)' : undefined }}>{absentToday}</div>
          <div className="kpi-sub">Not checked in</div>
        </div>
        <div className="kpi" style={{ cursor: 'pointer' }} onClick={() => navigate('/hr/leaves')}>
          <div className="kpi-label">Leave Requests</div>
          <div className="kpi-val" style={{ color: pendingLeaves.length > 0 ? 'var(--orange)' : undefined }}>{pendingLeaves.length}</div>
          <div className="kpi-sub">Pending approval</div>
        </div>
        <div className="kpi" style={{ cursor: 'pointer' }} onClick={() => navigate('/hr/payroll')}>
          <div className="kpi-label">Payroll</div>
          <div className="kpi-val">{payrollDone}</div>
          <div className="kpi-sub">{thisMonth} processed</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 4 }}>

        {/* Pending Leaves */}
        <div className="card">
          <div className="card-title">Pending Leave Requests</div>
          {pendingLeaves.length === 0 ? (
            <div className="empty" style={{ padding: '20px 0' }}>No pending requests</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {pendingLeaves.slice(0, 5).map(lr => (
                <div key={lr.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <div className="avatar" style={{ width: 32, height: 32, fontSize: 11, background: 'var(--accent-light)', color: 'var(--accent)', fontWeight: 700 }}>
                    {lr.full_name?.slice(0, 2).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{lr.full_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text2)' }}>
                      {lr.leave_type} · {new Date(lr.start_date).toLocaleDateString('en-IN')} – {new Date(lr.end_date).toLocaleDateString('en-IN')} ({lr.days}d)
                    </div>
                  </div>
                  <button className="btn-primary btn-sm" onClick={() => navigate('/hr/leaves')}>Review</button>
                </div>
              ))}
              {pendingLeaves.length > 5 && <div style={{ fontSize: 12, color: 'var(--text2)', textAlign: 'center', paddingTop: 4 }}>+{pendingLeaves.length - 5} more</div>}
            </div>
          )}
        </div>

        {/* Today's Attendance */}
        <div className="card">
          <div className="card-title">Today's Attendance</div>
          {todayAtt.length === 0 ? (
            <div className="empty" style={{ padding: '20px 0' }}>No check-ins yet today</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {todayAtt.slice(0, 6).map(a => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                  <span className={`pill ${a.status === 'present' ? 'pill-green' : a.status === 'half_day' ? 'pill-orange' : 'pill-red'}`}>
                    {a.status.replace('_', ' ')}
                  </span>
                  <span style={{ fontWeight: 500, fontSize: 13 }}>{a.full_name}</span>
                  {a.check_in && <span style={{ fontSize: 11, color: 'var(--text2)', marginLeft: 'auto' }}>
                    {new Date(a.check_in).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    {a.check_out ? ` – ${new Date(a.check_out).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : ' (in)'}
                  </span>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming Holidays */}
        <div className="card">
          <div className="card-title">Upcoming Holidays</div>
          {upcomingHolidays.length === 0 ? (
            <div className="empty" style={{ padding: '20px 0' }}>No upcoming holidays</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {upcomingHolidays.map(h => (
                <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {new Date(h.holiday_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{h.holiday_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text2)' }}>{h.holiday_type}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="card">
          <div className="card-title">Quick Actions</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { label: 'Add Employee', icon: '👤', path: '/hr/employees' },
              { label: 'Manual Attendance Entry', icon: '📋', path: '/hr/attendance' },
              { label: 'Run Payroll', icon: '💰', path: '/hr/payroll' },
              { label: 'Manage Holidays', icon: '📅', path: '/hr/holidays' },
              { label: 'Salary Structures', icon: '📊', path: '/hr/salary' },
            ].map(a => (
              <button key={a.path} className="btn" style={{ justifyContent: 'flex-start', gap: 10 }} onClick={() => navigate(a.path)}>
                <span>{a.icon}</span> {a.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
